import { useLingui } from '@lingui/react/macro'
import type { SearchHighlightData } from 'features/knowledgeDocumentViewer/components/viewer/search/SearchHighlightOverlay'
import { applyCenteredScale } from 'features/knowledgeDocumentViewer/components/viewer/shared/applyCenteredScale'
import { useAdjustedBoxes } from 'features/knowledgeDocumentViewer/components/viewer/shared/hooks/useAdjustedBoxes'
import { useAliasTooltip } from 'features/knowledgeDocumentViewer/components/viewer/shared/hooks/useAliasTooltip'
import { useFitToViewport } from 'features/knowledgeDocumentViewer/components/viewer/shared/hooks/useFitToViewport'
import { useLiveValueChips } from 'features/knowledgeDocumentViewer/components/viewer/shared/hooks/useLiveValueChips'
import { useShiftDragSelection } from 'features/knowledgeDocumentViewer/components/viewer/shared/hooks/useShiftDragSelection'
import { useZoomHotkeys } from 'features/knowledgeDocumentViewer/components/viewer/shared/hooks/useViewerHotkeys'
import { useViewerOverlayState } from 'features/knowledgeDocumentViewer/components/viewer/shared/hooks/useViewerOverlayState'
import {
  followFrameOnResize,
  type FrameTransform,
  holdFramedRegion,
  MAX_SCALE,
  useZoomControls,
} from 'features/knowledgeDocumentViewer/components/viewer/shared/hooks/useZoomControls'
import { PhotoCountBadgeLayer } from 'features/knowledgeDocumentViewer/components/viewer/shared/PhotoCountBadgeLayer'
import { PositionedTooltip } from 'features/knowledgeDocumentViewer/components/viewer/shared/PositionedTooltip'
import type { RegionFraming } from 'features/knowledgeDocumentViewer/components/viewer/shared/regionFraming'
import {
  ImageContainer,
  MultipageImageContainer,
  StyledImage,
  ViewerContainer,
} from 'features/knowledgeDocumentViewer/components/viewer/shared/styles'
import { SubjectTimeSeriesTooltip } from 'features/knowledgeDocumentViewer/components/viewer/shared/SubjectTimeSeriesTooltip'
import { transformWrapperMotionProps } from 'features/knowledgeDocumentViewer/components/viewer/shared/transformWrapperMotionProps'
import {
  Box,
  DocumentViewerHandle,
  TransformState,
  ViewerCommonProps,
} from 'features/knowledgeDocumentViewer/components/viewer/shared/types'
import { ViewerOverlaySvg } from 'features/knowledgeDocumentViewer/components/viewer/shared/ViewerOverlaySvg'
import { useDocumentTabDataContextOptional } from 'features/knowledgeDocumentViewer/contexts/DocumentTabDataContext'
import { useInstrumentSelectionOptional } from 'features/knowledgeDocumentViewer/contexts/InstrumentSelectionContext'
import { useKnowledgeDocumentUXContext } from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentUXContext'
import { EMPTY_PAGE_GRAPH_SOURCES } from 'features/knowledgeDocumentViewer/graph/canvasTypes'
import { setFlowScale } from 'features/knowledgeDocumentViewer/graph/flowScale'
import { projectNodeOverlays } from 'features/knowledgeDocumentViewer/graph/graphGeometry'
import { usePublishGraphNodeSelection } from 'features/knowledgeDocumentViewer/graph/GraphNodeSelectionContext'
import { GraphStatus } from 'features/knowledgeDocumentViewer/graph/GraphStatus'
import { GraphTooltip } from 'features/knowledgeDocumentViewer/graph/GraphTooltip'
import { getOverlayFocusBBox } from 'features/knowledgeDocumentViewer/graph/nodeFocusTarget'
import { useGraphInstrumentation } from 'features/knowledgeDocumentViewer/graph/useGraphInstrumentation'
import { useGraphOverlaySession } from 'features/knowledgeDocumentViewer/graph/useGraphOverlaySession'
import { useNodeFocusRequest } from 'features/knowledgeDocumentViewer/graph/useNodeFocusRequest'
import { usePageFlowAnimation } from 'features/knowledgeDocumentViewer/graph/usePageFlowAnimation'
import { usePageIsolation } from 'features/knowledgeDocumentViewer/graph/usePageIsolation'
import type { LiveValueDetailTier } from 'features/knowledgeDocumentViewer/hooks/useLiveProcessDataValues'
import {
  imageElementSrc,
  imageEventSrc,
} from 'features/knowledgeDocumentViewer/hooks/usePageImageRecovery'
import type { NormalizedBBox } from 'features/knowledgeDocumentViewer/types/ocrViewer'
import { useLabelerOptional } from 'features/labeler/LabelerContext'
import { useLabelerGestures } from 'features/labeler/useLabelerGestures'
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  ReactZoomPanPinchRef,
  TransformComponent,
  TransformWrapper,
} from 'react-zoom-pan-pinch'

export type ImageViewerRolodexOverride = {
  imageUrl: string | undefined
  isLoading: boolean
}

type ImageDimensions = [{ w: number; h: number }, { w: number; h: number }]

const EMPTY_IMAGE_DIMENSIONS: ImageDimensions = [
  { w: 0, h: 0 },
  { w: 0, h: 0 },
]

const noopMouse = () => {}

const DETAIL_TIER_BOUNDARIES_PX = [14, 22, 30]
const HYSTERESIS_UP = 1.05
const HYSTERESIS_DOWN = 0.95

/** Steps a detail tier toward the tier implied by the chip's on-screen height, applying hysteresis at each boundary so it does not flicker. */
function nextDetailTier(
  current: LiveValueDetailTier,
  screenChipHeight: number
): LiveValueDetailTier {
  let tier = current
  while (
    tier < 3 &&
    screenChipHeight > DETAIL_TIER_BOUNDARIES_PX[tier] * HYSTERESIS_UP
  ) {
    tier = (tier + 1) as LiveValueDetailTier
  }
  while (
    tier > 0 &&
    screenChipHeight < DETAIL_TIER_BOUNDARIES_PX[tier - 1] * HYSTERESIS_DOWN
  ) {
    tier = (tier - 1) as LiveValueDetailTier
  }
  return tier
}

export type ImageViewerProps = ViewerCommonProps & {
  imageUrls: string[]
  boxes: Box[]
  searchHighlights?: SearchHighlightData
  frameTarget?: {
    bbox: NormalizedBBox
    pageIndex: 0 | 1
    framing?: RegionFraming
  }
  onFrameApplied?: () => void
  restoredTransform?: TransformState | null
  documentId: string
  progressiveImageKey?: string
  isActiveTab: boolean
  transformWrapperRef?: React.MutableRefObject<HTMLElement | null>
  onImageLoaded?: (index: number, src: string) => void
  onImageError?: (index: number, src: string) => void
}

export const ImageViewerWithOverlay = forwardRef<
  DocumentViewerHandle,
  ImageViewerProps
>(function ImageViewerWithOverlay(
  {
    imageUrls,
    boxes,
    searchHighlights,
    frameTarget,
    onFrameApplied,
    restoredTransform = null,
    documentId,
    progressiveImageKey,
    isActiveTab,
    onImageLoaded,
    onImageError,
    onBoxClick,
    onBoxAuxClick,
    onBoxContextMenu,
    onBoxHover,
    onMultipleBoxSelect,
    onAliasClick,
    onDocReferenceClick,
    onDocReferenceHover,
    onResetZoom,
    onZoomStateChange,
    onTransformChange,
    onImageDimensionsChange,
    transformWrapperRef,
    highlightBoxIds,
    actionMode,
    showTooltips = false,
    pageGraphs = EMPTY_PAGE_GRAPH_SOURCES,
    graphPageNumber = 1,
    onOpenOffPageConnector,
  },
  ref
) {
  const { t } = useLingui()
  const transformRef = useRef<ReactZoomPanPinchRef | null>(null)
  const svgRef = useRef<SVGSVGElement | null>(null)
  const viewerContainerRef = useRef<HTMLDivElement | null>(null)
  const suppressNextBackgroundClickRef = useRef(false)
  const [minScale, setMinScale] = useState(0.05)
  const [detailTier, setDetailTier] = useState<LiveValueDetailTier>(3)
  const [glowPxPerImagePxStep, setGlowPxPerImagePxStep] = useState(0)
  const [loadedImages, setLoadedImages] = useState({
    pageKey: progressiveImageKey,
    dimensions: EMPTY_IMAGE_DIMENSIONS,
  })
  // Reset before image refs attach, so cached loads cannot race a reset effect.
  if (loadedImages.pageKey !== progressiveImageKey) {
    setLoadedImages({
      pageKey: progressiveImageKey,
      dimensions: EMPTY_IMAGE_DIMENSIONS,
    })
  }
  const imageDimensions =
    loadedImages.pageKey === progressiveImageKey
      ? loadedImages.dimensions
      : EMPTY_IMAGE_DIMENSIONS
  const isMultipage = imageUrls.length > 1

  const { activeTabId, currentSelectedBoxIds, updateDocumentHistoryItem } =
    useKnowledgeDocumentUXContext()
  const nodeOverlays = useMemo(() => {
    const pages = pageGraphs.flatMap(({ graph, pageNumber }) => {
      const index = pageNumber - graphPageNumber
      const size = imageDimensions[index]
      if (!size) return []
      const x = imageDimensions
        .slice(0, index)
        .reduce((sum, dimensions) => sum + dimensions.w, 0)
      return [
        {
          graph,
          pageNumber,
          bounds: {
            denormalized_x: x,
            denormalized_y: 0,
            denormalized_width: size.w,
            denormalized_height: size.h,
          },
        },
      ]
    })
    return projectNodeOverlays(pages)
  }, [pageGraphs, imageDimensions, graphPageNumber])
  const instrumentation = useGraphInstrumentation({
    pageGraphs,
    overlays: nodeOverlays,
    documentId,
    isActiveTab,
  })
  const isolation = usePageIsolation({ pageGraphs, isActiveTab })
  const { onStopFocused } = isolation
  const nodeSelection = usePublishGraphNodeSelection(
    pageGraphs,
    instrumentation.datapointsByNodeId
  )
  const flowLayer = usePageFlowAnimation({
    pageGraphs,
    overlays: nodeOverlays,
    isActiveTab,
    stops: isolation.stops,
    highlights: isolation.highlights,
    focus: isolation.focus,
    isolating: isolation.isolating,
  })
  const instrumentSelection = useInstrumentSelectionOptional()

  const handleBackgroundClick = useCallback(() => {
    if (suppressNextBackgroundClickRef.current) {
      suppressNextBackgroundClickRef.current = false
      return
    }

    instrumentSelection?.clear()
    if (currentSelectedBoxIds.length > 0) {
      updateDocumentHistoryItem(activeTabId, { selectedBoxIds: [] })
    }
  }, [
    activeTabId,
    currentSelectedBoxIds,
    updateDocumentHistoryItem,
    instrumentSelection,
  ])

  const combinedDimensions = useMemo(() => {
    if (!isMultipage) {
      return imageDimensions[0]
    }

    return {
      w: imageDimensions[0].w + imageDimensions[1].w,
      h: Math.max(imageDimensions[0].h, imageDimensions[1].h),
    }
  }, [imageDimensions, isMultipage])

  const adjustedBoxes = useAdjustedBoxes(
    boxes,
    isMultipage,
    imageDimensions[0].w
  )

  const { chips: liveChips, chipRectByBoxId } = useLiveValueChips({
    boxes: adjustedBoxes,
    imageNatural: combinedDimensions,
    firstPageWidth: imageDimensions[0].w,
    isMultipage,
    detailTier,
  })

  const medianChipHeight = useMemo(() => {
    if (liveChips.length === 0) return 0
    const heights = liveChips.map((chip) => chip.height).sort((a, b) => a - b)
    const mid = Math.floor(heights.length / 2)
    return heights.length % 2 === 0
      ? (heights[mid - 1] + heights[mid]) / 2
      : heights[mid]
  }, [liveChips])

  const {
    fillFor,
    getMatchedDatapoints,
    handleBoxMouseEnter,
    handleBoxMouseLeave,
    hasTimeSeriesData,
    isLoadingOffPageConnector,
    offPageConnectorDocuments,
    setInternalHighlightedBoxIds,
    strokeFor,
    tooltipBox,
    tooltipLinkedCount,
    tooltipPosition,
    updateTooltipPosition,
  } = useViewerOverlayState({
    adjustedBoxes,
    highlightBoxIds,
    actionMode,
    showTooltips,
    transformRef,
    viewerContainerRef,
    onBoxHover,
    chipRectByBoxId,
  })

  const applyImageDimensions = useCallback(
    (index: number, el: HTMLImageElement) => {
      const { naturalWidth, naturalHeight } = el
      if (naturalWidth === 0 || naturalHeight === 0) return
      setLoadedImages((prev) => {
        const dimensions =
          prev.pageKey === progressiveImageKey
            ? prev.dimensions
            : EMPTY_IMAGE_DIMENSIONS
        if (
          dimensions[index].w === naturalWidth &&
          dimensions[index].h === naturalHeight
        ) {
          return prev
        }
        const next: ImageDimensions = [...dimensions]
        next[index] = { w: naturalWidth, h: naturalHeight }
        return { pageKey: progressiveImageKey, dimensions: next }
      })
    },
    [progressiveImageKey]
  )

  const handleImageLoad = useCallback(
    (index: number) => (event: React.SyntheticEvent<HTMLImageElement>) => {
      applyImageDimensions(index, event.currentTarget)
      onImageLoaded?.(index, imageEventSrc(event))
    },
    [applyImageDimensions, onImageLoaded]
  )

  const handleImageError = useCallback(
    (index: number) => (event: React.SyntheticEvent<HTMLImageElement>) =>
      onImageError?.(index, imageEventSrc(event)),
    [onImageError]
  )

  const onImageLoadedRef = useRef(onImageLoaded)
  onImageLoadedRef.current = onImageLoaded

  // Cached images may already be complete when the ref attaches (e.g. after a
  // StrictMode remount), so the load event alone can miss dimensions.
  const imageRefs = useMemo(
    () =>
      EMPTY_IMAGE_DIMENSIONS.map(
        (_, index) =>
          (el: HTMLImageElement | null): void => {
            if (!el?.complete) return
            applyImageDimensions(index, el)
            if (el.naturalWidth > 0) {
              onImageLoadedRef.current?.(index, imageElementSrc(el))
            }
          }
      ),
    [applyImageDimensions]
  )

  const allImagesLoaded = useMemo(() => {
    if (!isMultipage) {
      return imageDimensions[0].w > 0 && imageDimensions[0].h > 0
    }

    return (
      imageDimensions[0].w > 0 &&
      imageDimensions[0].h > 0 &&
      imageDimensions[1].w > 0 &&
      imageDimensions[1].h > 0
    )
  }, [imageDimensions, isMultipage])

  const { imgNatural, resetToFit, onFitComplete } = useFitToViewport(
    transformRef,
    setMinScale,
    combinedDimensions,
    isMultipage,
    allImagesLoaded,
    8,
    true,
    restoredTransform,
    progressiveImageKey ?? documentId,
    progressiveImageKey !== undefined
  )

  const updateDetailTier = useCallback(() => {
    const instance = transformRef.current?.instance
    if (!instance || !imgNatural.w) return
    const contentComponent = instance.contentComponent
    if (!contentComponent) return
    const scale = instance.transformState.scale
    const pxPerImagePx = (scale * contentComponent.offsetWidth) / imgNatural.w

    setFlowScale(svgRef.current, pxPerImagePx)
    const step = Math.round(Math.log(pxPerImagePx) / Math.log(1.25))
    setGlowPxPerImagePxStep((current) => (current === step ? current : step))

    if (!medianChipHeight) return
    const screenChipHeight = medianChipHeight * pxPerImagePx
    setDetailTier((current) => nextDetailTier(current, screenChipHeight))
  }, [medianChipHeight, imgNatural.w])

  useEffect(() => {
    updateDetailTier()
  }, [updateDetailTier])

  const liveChipGlowPxPerImagePx = Math.pow(1.25, glowPxPerImagePxStep)

  const zoomIn = useCallback(() => {
    applyCenteredScale(transformRef.current, (currentScale) =>
      Math.min(currentScale * 2, 10)
    )
  }, [])

  const zoomOut = useCallback(() => {
    applyCenteredScale(transformRef.current, (currentScale) =>
      Math.max(currentScale / 2, minScale)
    )
  }, [minScale])

  const { zoomToBox, panToRegion, frameRegion, resetZoom } = useZoomControls(
    adjustedBoxes,
    imgNatural,
    transformRef,
    resetToFit,
    onResetZoom,
    minScale
  )

  useZoomHotkeys({
    enabled: isActiveTab,
    onResetZoom: resetZoom,
    onZoomIn: zoomIn,
    onZoomOut: zoomOut,
  })

  const onFrameAppliedRef = useRef(onFrameApplied)
  onFrameAppliedRef.current = onFrameApplied

  // Run after useFitToViewport so fit lands first; this effect re-applies the
  // frame on top whenever imagery is ready. Depends on onFitComplete so a same-
  // commit fit cannot overwrite the frame.
  useEffect(() => {
    if (!frameTarget || !allImagesLoaded || !onFitComplete) return
    // Same combined-image layout as SearchHighlightOverlay.getPageLayout.
    const firstPageWidth = imageDimensions[0].w
    const onRightPage = isMultipage && frameTarget.pageIndex === 1
    const pageWidth = !isMultipage
      ? combinedDimensions.w
      : onRightPage
        ? combinedDimensions.w - firstPageWidth
        : firstPageWidth
    const xOffset = onRightPage ? firstPageWidth : 0
    const { bbox, framing } = frameTarget
    const mappedBbox = {
      left: (xOffset + bbox.left * pageWidth) / combinedDimensions.w,
      top: bbox.top,
      width: (bbox.width * pageWidth) / combinedDimensions.w,
      height: bbox.height,
    }
    let lastFrame: FrameTransform | null = null
    const apply = (animationTime: number) => {
      const applied = frameRegion(mappedBbox, animationTime, framing)
      if (applied) lastFrame = applied
      return applied
    }
    const readScale = () => {
      const instance = transformRef.current?.instance
      if (!instance) return undefined
      return instance.animation && lastFrame
        ? lastFrame.scale
        : instance.transformState.scale
    }
    const releaseHold = holdFramedRegion(apply, readScale, {
      onApplied: () => onFrameAppliedRef.current?.(),
      expectedScale: () => lastFrame?.scale,
    })
    const stopFollowing = followFrameOnResize(
      transformRef.current,
      () => lastFrame,
      apply
    )
    return () => {
      releaseHold()
      stopFollowing()
    }
  }, [
    frameTarget,
    allImagesLoaded,
    onFitComplete,
    imageDimensions,
    isMultipage,
    combinedDimensions,
    frameRegion,
  ])

  // Marquee must include alias highlights — fusion-era docs often lack OCR boxes.
  const tabData = useDocumentTabDataContextOptional()
  const aliasPseudoBoxes = useMemo<Box[]>(() => {
    if (!instrumentSelection || !tabData || !imgNatural.w) return []
    const { aliasHighlights, pageNumber } = tabData
    const firstPageWidth = imageDimensions[0].w
    return aliasHighlights
      .filter(
        (highlight) =>
          highlight.page === pageNumber ||
          (isMultipage && highlight.page === pageNumber + 1)
      )
      .map((highlight, idx) => {
        const onRightPage = isMultipage && highlight.page > pageNumber
        const pageWidth = !isMultipage
          ? imgNatural.w
          : onRightPage
            ? imgNatural.w - firstPageWidth
            : firstPageWidth
        const xOffset = onRightPage ? firstPageWidth : 0
        return {
          id: `alias:${highlight.subjectUid}:${idx}`,
          confirmed: true,
          x: xOffset + highlight.bbox.left * pageWidth,
          y: highlight.bbox.top * imgNatural.h,
          w: highlight.bbox.width * pageWidth,
          h: highlight.bbox.height * imgNatural.h,
          label: highlight.label,
        }
      })
  }, [instrumentSelection, tabData, imgNatural, imageDimensions, isMultipage])

  const { handleAliasHover, aliasTooltipProps } = useAliasTooltip({
    showTooltips,
    containerRef: viewerContainerRef,
  })
  const {
    nodeInspection,
    nodeOverlayLayer,
    suspend: suspendNodeOverlayInspection,
    dismiss: dismissNodeOverlayInspection,
    isInspecting: isNodeInspectionActive,
  } = useGraphOverlaySession({
    overlays: nodeOverlays,
    liveValueAppearances: instrumentation.appearances,
    svgRef,
    containerRef: viewerContainerRef,
    onBoxMouseLeave: handleBoxMouseLeave,
    onAliasHover: handleAliasHover,
    onDocReferenceHover,
    onOpenOffPageConnector,
    onSelectFlowNode: isolation.onSelectNode,
    onSelectNode: nodeSelection.selectNode,
    selectedNodeId: nodeSelection.selectedNodeId,
  })

  const focusStop = useCallback(
    (id: string): boolean => {
      const bbox = getOverlayFocusBBox(nodeOverlays, id, imgNatural)
      if (!bbox) return false
      suspendNodeOverlayInspection()
      const framed = frameRegion(bbox) !== null
      if (framed) onStopFocused(id)
      return framed
    },
    [
      nodeOverlays,
      imgNatural,
      suspendNodeOverlayInspection,
      frameRegion,
      onStopFocused,
    ]
  )
  useNodeFocusRequest({ pageGraphs, isActiveTab, focus: focusStop })

  const marqueeBoxes = useMemo(
    () =>
      aliasPseudoBoxes.length > 0
        ? [...adjustedBoxes, ...aliasPseudoBoxes]
        : adjustedBoxes,
    [adjustedBoxes, aliasPseudoBoxes]
  )

  const {
    isShiftPressed,
    isDragging,
    selectionBox,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
  } = useShiftDragSelection({
    enabled: isActiveTab,
    svgRef,
    boxes: marqueeBoxes,
    onSelectionComplete: (selectedIds) => {
      suppressNextBackgroundClickRef.current = true
      const selectedIdSet = new Set(selectedIds)
      onMultipleBoxSelect?.(
        selectedIds,
        marqueeBoxes.filter((box) => selectedIdSet.has(box.id))
      )
    },
    imageNaturalDimensions: imgNatural,
  })

  // Labeling mode owns drag gestures; viewer marquee/pan yields to them.
  const labeler = useLabelerOptional()
  const labelerActive = labeler?.active ?? false
  const noopBbox = useCallback(() => {}, [])
  const labelerSelectedBox = useMemo(() => {
    if (!labelerActive || !labeler?.selectedBoxId) return null
    return (
      (labeler.page?.boxes ?? []).find((b) => b.id === labeler.selectedBoxId) ??
      null
    )
  }, [labelerActive, labeler?.selectedBoxId, labeler?.page?.boxes])
  const labelerGestures = useLabelerGestures({
    svgRef,
    imageNaturalDimensions: imgNatural,
    // Compare mode: stand down gestures; allow pan.
    active: labelerActive && !(labeler?.compareMode ?? false),
    isDrawingMode: labelerActive && (labeler?.isDrawingMode ?? false),
    selectedBox: labelerSelectedBox,
    onDraw: labeler?.draw ?? noopBbox,
    onRubberBand: labeler?.rubberBandSelect ?? noopBbox,
    onCommitBbox: labeler?.commitBbox ?? noopBbox,
    onLiveBbox: labeler?.setLiveBbox ?? noopBbox,
    onDraftChange: labeler?.setGestureDraft,
  })
  const labelerDrawArmed =
    labelerActive &&
    !(labeler?.compareMode ?? false) &&
    ((labeler?.isDrawingMode ?? false) || labelerGestures.isShiftPressed)
  const overlayMouseHandlers = labelerActive
    ? // Gestures hook listens natively; React mouse slots stay inert.
      { onMouseDown: noopMouse, onMouseMove: noopMouse, onMouseUp: noopMouse }
    : {
        onMouseDown: handleMouseDown,
        onMouseMove: handleMouseMove,
        onMouseUp: handleMouseUp,
      }
  // Labeling drafts live in LabelerOverlay; hide the viewer marquee while active.
  const overlayIsDragging = labelerActive ? false : isDragging
  const overlaySelectionBox = labelerActive ? null : selectionBox

  const checkAndNotifyZoomState = useCallback(() => {
    if (!transformRef.current || !onZoomStateChange) return

    const currentScale = transformRef.current.instance.transformState.scale
    const epsilon = 0.001

    onZoomStateChange({
      isAtMinZoom: Math.abs(currentScale - minScale) < epsilon,
      isAtMaxZoom:
        Math.abs(currentScale - MAX_SCALE) < epsilon ||
        currentScale >= MAX_SCALE,
    })
  }, [minScale, onZoomStateChange])

  useEffect(() => {
    checkAndNotifyZoomState()
  }, [checkAndNotifyZoomState])

  useEffect(() => {
    if (!imgNatural.w || !imgNatural.h) {
      return
    }

    onImageDimensionsChange?.(imgNatural)
  }, [imgNatural, onImageDimensionsChange])

  const getCurrentTransformState = useCallback((): TransformState | null => {
    const currentState = transformRef.current?.instance.transformState
    if (!currentState) {
      return null
    }

    return {
      scale: currentState.scale,
      positionX: currentState.positionX,
      positionY: currentState.positionY,
    }
  }, [])

  const handleTransformInit = useCallback(
    (pinchRef: ReactZoomPanPinchRef) => {
      if (!transformWrapperRef) return
      transformWrapperRef.current = pinchRef.instance.wrapperComponent ?? null
    },
    [transformWrapperRef]
  )

  useEffect(() => {
    return () => {
      if (transformWrapperRef) {
        transformWrapperRef.current = null
      }
    }
  }, [transformWrapperRef])

  const handleTransformed = useCallback(() => {
    suspendNodeOverlayInspection()
    checkAndNotifyZoomState()
    updateTooltipPosition()
    updateDetailTier()

    // Skip pre-fit transform noise so we do not persist it as a restored viewport.
    if (!onFitComplete) return

    const currentTransformState = getCurrentTransformState()
    if (currentTransformState) {
      onTransformChange?.(currentTransformState)
    }
  }, [
    checkAndNotifyZoomState,
    suspendNodeOverlayInspection,
    getCurrentTransformState,
    onFitComplete,
    onTransformChange,
    updateDetailTier,
    updateTooltipPosition,
  ])

  useImperativeHandle(
    ref,
    () => ({
      zoomToBox,
      panToRegion,
      setHighlightedBoxIds: setInternalHighlightedBoxIds,
      resetZoom,
      zoomIn,
      zoomOut,
      setTransform: (posX, posY, nextScale, animTime = 0) => {
        transformRef.current?.setTransform(posX, posY, nextScale, animTime)
      },
      getViewportInfo: () => {
        const wrapper = transformRef.current?.instance.wrapperComponent
        if (!wrapper) {
          return null
        }

        return {
          wrapperWidth: wrapper.clientWidth,
          wrapperHeight: wrapper.clientHeight,
        }
      },
      getImageNaturalDimensions: () => imgNatural,
    }),
    [
      imgNatural,
      zoomToBox,
      panToRegion,
      setInternalHighlightedBoxIds,
      resetZoom,
      zoomIn,
      zoomOut,
    ]
  )

  return (
    <ViewerContainer
      ref={viewerContainerRef}
      style={{
        cursor: isShiftPressed || labelerDrawArmed ? 'crosshair' : 'default',
      }}>
      <TransformWrapper
        ref={transformRef}
        minScale={minScale}
        maxScale={10}
        limitToBounds
        wheel={{ step: 0.5 }}
        doubleClick={{ disabled: labelerDrawArmed, mode: 'zoomIn' }}
        pinch={{ disabled: false }}
        panning={{ disabled: labelerDrawArmed }}
        {...transformWrapperMotionProps()}
        onInit={handleTransformInit}
        onTransformed={handleTransformed}>
        <TransformComponent
          wrapperStyle={{ width: '100%', height: '100%' }}
          contentStyle={{ display: 'inline-block' }}>
          <ImageContainer
            $opacity={onFitComplete ? 1 : 0}
            $width={imgNatural ? `${imgNatural.w}px` : 'auto'}
            $height={imgNatural ? `${imgNatural.h}px` : 'auto'}>
            {isMultipage ? (
              <MultipageImageContainer>
                {imageUrls.map((url, index) => {
                  const pageNumber = index + 1
                  return (
                    <StyledImage
                      key={url}
                      ref={imageRefs[index]}
                      src={url}
                      alt={t`diagram page ${pageNumber}`}
                      onLoad={handleImageLoad(index)}
                      onError={handleImageError(index)}
                      draggable={false}
                    />
                  )
                })}
              </MultipageImageContainer>
            ) : (
              <StyledImage
                key={progressiveImageKey}
                ref={imageRefs[0]}
                src={imageUrls[0]}
                alt={t`diagram`}
                onLoad={handleImageLoad(0)}
                onError={handleImageError(0)}
                draggable={false}
              />
            )}

            {!!imgNatural && (
              <ViewerOverlaySvg
                actionMode={actionMode}
                nodeOverlayLayer={nodeOverlayLayer}
                boxes={adjustedBoxes}
                disableBackgroundClick={isShiftPressed || labelerDrawArmed}
                fillFor={fillFor}
                firstPageWidth={imageDimensions[0].w}
                hasTimeSeriesData={hasTimeSeriesData}
                imageNatural={imgNatural}
                isDragging={overlayIsDragging}
                isMultipage={isMultipage}
                liveChipGlowPxPerImagePx={liveChipGlowPxPerImagePx}
                liveChips={liveChips}
                searchHighlights={searchHighlights}
                onBackgroundClick={handleBackgroundClick}
                onBoxAuxClick={onBoxAuxClick}
                onBoxClick={onBoxClick}
                onBoxContextMenu={onBoxContextMenu}
                onBoxMouseEnter={handleBoxMouseEnter}
                onBoxMouseLeave={handleBoxMouseLeave}
                onAliasClick={onAliasClick}
                onAliasHover={handleAliasHover}
                onDocReferenceClick={onDocReferenceClick}
                onDocReferenceHover={onDocReferenceHover}
                onMouseDown={overlayMouseHandlers.onMouseDown}
                onMouseMove={overlayMouseHandlers.onMouseMove}
                onMouseUp={overlayMouseHandlers.onMouseUp}
                selectionBox={overlaySelectionBox}
                strokeFor={strokeFor}
                svgRef={svgRef}
                flowLayer={flowLayer}
              />
            )}
            {!!imgNatural && (
              <PhotoCountBadgeLayer
                imageNatural={imgNatural}
                firstPageWidth={imageDimensions[0].w}
                isMultipage={isMultipage}
                counterZoom
              />
            )}
          </ImageContainer>
        </TransformComponent>
      </TransformWrapper>
      {!isNodeInspectionActive &&
        showTooltips &&
        tooltipBox &&
        tooltipPosition && (
          <PositionedTooltip
            tooltipBox={tooltipBox}
            tooltipPosition={tooltipPosition}
            viewportWidth={viewerContainerRef.current?.clientWidth ?? 1200}
            viewportHeight={viewerContainerRef.current?.clientHeight ?? 800}
            hasTimeSeriesData={hasTimeSeriesData}
            getMatchedDatapoints={getMatchedDatapoints}
            offPageConnectorDocuments={offPageConnectorDocuments}
            isLoadingOffPageConnector={isLoadingOffPageConnector}
            tooltipLinkedCount={tooltipLinkedCount}
          />
        )}
      {instrumentation.message && (
        <GraphStatus
          message={instrumentation.message}
          failed
          retry={instrumentation.retry}
        />
      )}
      {nodeInspection && (
        <GraphTooltip
          inspection={nodeInspection}
          datapoints={instrumentation.datapointsByNodeId.get(
            nodeInspection.overlay.node.id
          )}
          onDismiss={dismissNodeOverlayInspection}
        />
      )}
      {!nodeInspection && aliasTooltipProps && (
        <SubjectTimeSeriesTooltip {...aliasTooltipProps} />
      )}
    </ViewerContainer>
  )
})

export default ImageViewerWithOverlay
